import { ExitRuleExitsByNode } from 'modules/workflowIndex/types';
import { Node } from 'modules/workflows/types';
import { Button, BUTTON_VARIANTS, BUTTON_SIZES } from 'ui/button';
import { Nest16 } from 'ui/icons';
import styled from 'styled-components';
import { ContactCountItem, SendActivity } from './ContactCounts.types';
import { ContactCount } from './ContactCounts.constants';
import { BreakdownType } from '../../takeovers/JourneyVisitorBreakdownTakeover/JourneyVisitorBreakdown.constant';

export const CONTACT_COUNT_BUTTON_TEST_ID = 'contact-count-button';

const ExitRuleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
const ExitButton = styled(Button)`
  padding: 0;
`;

export const formatCountValue = (count: number | undefined) => count?.toLocaleString() ?? '0';

export const reduceExitRuleExitsByNode = (sum: number, ruleObj: { [ruleId: string]: number }) => {
  if (!ruleObj) return sum;
  const tempSum = Object.values(ruleObj).reduce(
    (ruleSum: number, ruleNum: number) => ruleSum + ruleNum,
    0,
  );
  return sum + tempSum;
};

export const formatExitRuleCount = (exitRuleExitsByNode: ExitRuleExitsByNode | undefined) => {
  if (!exitRuleExitsByNode) return 0;
  const count = Object.values(exitRuleExitsByNode).reduce(reduceExitRuleExitsByNode, 0);

  return count;
};

export const getDefaultContactCountsJVC = () => {
  return [
    {
      key: ContactCount.AllStarted,
      text: 'Entries:',
      value: '--',
      onClick: undefined,
    },
    {
      key: ContactCount.AllCompleted,
      text: 'Exits:',
      value: '--',
      onClick: undefined,
    },
    {
      key: ContactCount.ExitRules,
      text: 'Exits by rules:',
      value: '--',
      onClick: undefined,
    },
  ];
};

const generateRowItemObj = (
  key: ContactCount,
  text: string | JSX.Element,
  value: number | undefined,
  onClick: () => void,
) => ({
  key,
  text,
  value: formatCountValue(value),
  ...((value || 0) > 0 ? { onClick } : {}),
});

export const compileExitRuleStat = (
  text: string | JSX.Element,
  exitRuleExitsByNode: ExitRuleExitsByNode | undefined,
  openJourneyVisitorBreakdown: (breakdownType: BreakdownType) => () => void,
) => {
  const exitRuleCount = formatExitRuleCount(exitRuleExitsByNode);
  return generateRowItemObj(
    ContactCount.ExitRules,
    text,
    exitRuleCount,
    openJourneyVisitorBreakdown(BreakdownType.VisitorsExitedByRule),
  );
};

export const getSelectedNodeAnalytics = (
  sendActivity: SendActivity,
  selectedNode: Node,
): ContactCountItem[] => {
  const { id, numUsers } = selectedNode;
  const selectedExitRuleStats = {
    [id]: sendActivity?.exitRuleExitsByNode?.[id],
  } as ExitRuleExitsByNode;
  return [
    {
      key: ContactCount.InProgress,
      text: 'Users currently in tile:',
      value: numUsers || 0,
    },
    compileExitRuleStat('Users exited here:', selectedExitRuleStats, () => ({}) as () => void),
  ];
};

export const getContactCountsJVC = (
  journeyVistorCounts: {
    visitorsEntered?: number;
    visitorsFinished?: number;
    visitorsExitedByRules?: number;
  },
  handleOpenExitRulesDetails: () => void,
  openJourneyVisitorBreakdown: (breakdownType: BreakdownType, numUserTotal?: number) => () => void,
): ContactCountItem[] => {
  const exitRuleLine = (
    <ExitRuleContainer>
      <div>Exits by rules:</div>
      {(journeyVistorCounts?.visitorsExitedByRules || 0) > 0 && (
        <ExitButton
          data-test={CONTACT_COUNT_BUTTON_TEST_ID}
          variant={BUTTON_VARIANTS.TEXT}
          size={BUTTON_SIZES.MEDIUM}
          onClick={handleOpenExitRulesDetails}
        >
          <Nest16 />
          View details
        </ExitButton>
      )}
    </ExitRuleContainer>
  );

  const contactCounts = [
    generateRowItemObj(
      ContactCount.AllStarted,
      'Entries:',
      journeyVistorCounts?.visitorsEntered,
      openJourneyVisitorBreakdown(
        BreakdownType.VisitorsEntered,
        journeyVistorCounts?.visitorsEntered,
      ),
    ),
    generateRowItemObj(
      ContactCount.AllCompleted,
      'Exits:',
      journeyVistorCounts?.visitorsFinished,
      openJourneyVisitorBreakdown(
        BreakdownType.VisitorsExited,
        journeyVistorCounts?.visitorsFinished,
      ),
    ),
    generateRowItemObj(
      ContactCount.ExitRules,
      exitRuleLine,
      journeyVistorCounts?.visitorsExitedByRules,
      openJourneyVisitorBreakdown(
        BreakdownType.VisitorsExitedByRule,
        journeyVistorCounts?.visitorsExitedByRules,
      ),
    ),
  ];
  return contactCounts;
};

export const getContactCounts = (
  sendActivity: SendActivity,
  handleOpenExitRulesDetails: () => void,
  inProgressCount: number,
  openJourneyVisitorBreakdown: (breakdownType: BreakdownType, numUserTotal?: number) => () => void,
): ContactCountItem[] => {
  const exitRuleCount = formatExitRuleCount(sendActivity?.exitRuleExitsByNode);
  const exitRuleLine = (
    <ExitRuleContainer>
      <div>Exits by rules:</div>
      {exitRuleCount > 0 && (
        <ExitButton
          data-test={CONTACT_COUNT_BUTTON_TEST_ID}
          variant={BUTTON_VARIANTS.TEXT}
          size={BUTTON_SIZES.MEDIUM}
          onClick={handleOpenExitRulesDetails}
        >
          <Nest16 />
          View details
        </ExitButton>
      )}
    </ExitRuleContainer>
  );

  const contactCounts = [
    generateRowItemObj(
      ContactCount.AllStarted,
      'Entries:',
      inProgressCount + (sendActivity?.finished ?? 0),
      openJourneyVisitorBreakdown(
        BreakdownType.VisitorsEntered,
        inProgressCount + (sendActivity?.finished ?? 0),
      ),
    ),
    generateRowItemObj(
      ContactCount.AllCompleted,
      'Exits:',
      sendActivity?.finished,
      openJourneyVisitorBreakdown(BreakdownType.VisitorsExited, sendActivity?.finished),
    ),
    generateRowItemObj(
      ContactCount.ExitRules,
      exitRuleLine,
      exitRuleCount,
      openJourneyVisitorBreakdown(BreakdownType.VisitorsExitedByRule, exitRuleCount),
    ),
  ];
  return contactCounts;
};
